/*eslint-disable*/
import React, { useEffect } from "react";
import {
    MenuItem,
    Select,
    FormControl,
    SelectChangeEvent,
    Grid,
} from "@mui/material";
import {
    DateRange,
    LocalizationProvider,
    DateRangePicker,
    PickersShortcutsItem,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import dayjs from "dayjs"; // Ensure correct import
import "dayjs/locale/en-gb";

interface FilterGridProps {
    selectedUser: string;
    selectedWarehouse: string;
    dateRange: [Dayjs | null, Dayjs | null];
    uniqueUsers: { user_id: string; first_name: string; last_name: string }[];
    uniqueWarehouses: string[];
    onUserChange: (value: string) => void;
    onWarehouseChange: (value: string) => void;
    onDateRangeChange: (newDateRange: [Dayjs | null, Dayjs | null]) => void;
}

const FilterGrid: React.FC<FilterGridProps> = ({
    selectedUser,
    selectedWarehouse,
    dateRange,
    uniqueUsers,
    uniqueWarehouses,
    onUserChange,
    onWarehouseChange,
    onDateRangeChange,
}) => {
    const shortcuts: PickersShortcutsItem<DateRange<Dayjs>>[] = [
        {
            label: "Cette semaine",
            getValue: () => [
                dayjs().subtract(1, "week").startOf("week"),
                dayjs().endOf("week"),
            ],
        },
        {
            label: "Ce mois",
            getValue: () => [
                dayjs().startOf("month"),
                dayjs().isBefore(dayjs().endOf("month"))
                    ? dayjs()
                    : dayjs().endOf("month"),
            ],
        },
        {
            label: "Cette année",
            getValue: () => [
                dayjs().startOf("year"),
                dayjs().isBefore(dayjs().endOf("year"))
                    ? dayjs()
                    : dayjs().endOf("year"),
            ],
        },
    ];

    useEffect(() => {
        if (
            uniqueWarehouses.length === 1 &&
            selectedWarehouse !== uniqueWarehouses[0]
        ) {
            onWarehouseChange(uniqueWarehouses[0]);
        }
    }, [uniqueWarehouses, selectedWarehouse, onWarehouseChange]);

    return (
        <div className="w-full">
            <Grid
                container
                spacing={2}
                mb={2}
                columns={{ xs: 1, md: 3 }}
                justifyContent="flex-end"
            >
                <Grid size={1}>
                    <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale="en-gb"
                    >
                        <DateRangePicker
                            localeText={{ start: "Début", end: "Fin" }}
                            value={dateRange}
                            onChange={(newValue) => {
                                if (newValue) {
                                    const [start, end] = newValue;
                                    onDateRangeChange([
                                        start ? dayjs(start) : null,
                                        end ? dayjs(end) : null,
                                    ]);
                                }
                            }}
                            format="DD/MM/YYYY"
                            slotProps={{
                                shortcuts: {
                                    items: shortcuts,
                                },
                                textField: {
                                    fullWidth: true,
                                    size: "small",
                                    InputProps: {
                                        style: {
                                            fontSize: "0.75rem",
                                        },
                                    },
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Grid>

                <Grid size={1}>
                    <FormControl fullWidth>
                        <Select
                            value={selectedWarehouse}
                            onChange={(e: SelectChangeEvent<string>) =>
                                onWarehouseChange(e.target.value)
                            }
                            size="small"
                            sx={{ fontSize: "0.75rem" }}
                        >
                            <MenuItem value="all">Tous les entrepôts</MenuItem>
                            {uniqueWarehouses.map((warehouse) => (
                                <MenuItem key={warehouse} value={warehouse}>
                                    {warehouse}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid size={1}>
                    <FormControl fullWidth>
                        <Select
                            value={selectedUser}
                            onChange={(e: SelectChangeEvent<string>) =>
                                onUserChange(e.target.value)
                            }
                            size="small"
                            sx={{ fontSize: "0.75rem" }}
                        >
                            <MenuItem value="all">
                                Tous les utilisateurs
                            </MenuItem>
                            {uniqueUsers.map((user) => (
                                <MenuItem
                                    key={user.user_id}
                                    value={`${user.first_name} ${user.last_name}`}
                                >
                                    {user.first_name} {user.last_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </div>
    );
};

export default FilterGrid;
